import { useEffect, useRef } from "react"
import type { PageInfo } from "../table.types"
import {
  SUMMARY_LOAD_ALL_MAX_ATTEMPTS,
  SUMMARY_LOAD_ALL_MAX_ROWS,
  SUMMARY_LOAD_ALL_PAGE_SIZE,
} from "./table.summary.constants"

export type UseSummaryLoadAllInput = {
  /** The summary row is showing over rows. */
  isSummaryShown: boolean
  /**
   * The server's paging facts. Absent on a client-loaded table, which holds
   * every row already and so has nothing to fetch.
   */
  pageInfo: PageInfo | null | undefined
  /** True while any load is under way — a first page, a refetch, a next page. */
  isLoading: boolean
  rowCount: number
  /**
   * Loads the next page of `limit` rows and says whether a load started;
   * guarded by the caller (nothing while one is under way), shared with
   * scrolling.
   */
  fetchMore(limit: number): boolean
}

/**
 * A summary describes the whole view, not the rows the operator happened to
 * scroll to. So while the row shows on a server-paginated list with pages
 * still unfetched, this fetches them — in large pages, one after the other
 * as each lands — the way scrolling to the end would, without the scrolling.
 * Lists past `SUMMARY_LOAD_ALL_MAX_ROWS` are left as they are.
 *
 * A page that fails to grow the list is retried once, then let be, so a
 * failing request is never hammered. The budget comes back when the list
 * moves again: rows arrive (a scroll), or a load this hook didn't ask for
 * runs (a filter change, a refetch) — that one replaces the list outright,
 * and can't be told from a stall by row count alone, since every filter's
 * first page is exactly one page of rows.
 *
 * Reports whether the summary is still partial, so the row can show it.
 */
export function useSummaryLoadAll(input: UseSummaryLoadAllInput): {
  isPartial: boolean
} {
  const { isSummaryShown, pageInfo, isLoading, rowCount, fetchMore } = input
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const isWithinReach = (pageInfo?.totalCount ?? 0) <= SUMMARY_LOAD_ALL_MAX_ROWS
  const isWanted = isSummaryShown && hasNextPage && isWithinReach

  const fetchMoreRef = useRef(fetchMore)
  fetchMoreRef.current = fetchMore
  const trackerRef = useRef({ rowCount: 0, attempts: 0, isAwaiting: false })

  useEffect(() => {
    const tracker = trackerRef.current
    if (isLoading) {
      if (!tracker.isAwaiting) tracker.attempts = 0
      return
    }
    tracker.isAwaiting = false
    if (!isWanted) return
    if (rowCount !== tracker.rowCount) {
      tracker.rowCount = rowCount
      tracker.attempts = 0
    }
    if (tracker.attempts >= SUMMARY_LOAD_ALL_MAX_ATTEMPTS) return
    tracker.attempts += 1
    tracker.isAwaiting = fetchMoreRef.current(SUMMARY_LOAD_ALL_PAGE_SIZE)
  }, [isWanted, isLoading, rowCount])

  return { isPartial: hasNextPage }
}
